import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Sparkles, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import apiClient from '@/services/apiClient';
import { useAuthStore } from '@/stores/useAuthStore';
import { useMyProfile, useUpdateMyPreferences } from '@/hooks/useMyProfile';

const BANNER_HEIGHT = '40px';

const setBannerHeightVar = (value: string) => {
  if (typeof document === 'undefined') return;
  document.documentElement.style.setProperty('--maie-banner-h', value);
  // Push regular flow content below the banner. Fixed top-0 elements
  // (sidebar, page headers) won't shift — those should adopt
  // `top: var(--maie-banner-h, 0px)` if they need to clear the banner.
  document.body.style.paddingTop = value;
};

interface Announcement {
  id: string;
  message: string;
}

interface ApiEnvelope<T> {
  success: boolean;
  data: T;
}

const unwrap = <T,>(payload: ApiEnvelope<T> | T): T =>
  payload &&
  typeof payload === 'object' &&
  'success' in (payload as object) &&
  'data' in (payload as object)
    ? (payload as ApiEnvelope<T>).data
    : (payload as T);

/**
 * Platform announcement banner (formerly the pilot "beta banner").
 *
 * Content is server-driven: the latest enabled row in
 * `platform_announcements`, published from the Console's Flags page.
 * Dismissal is PER ACCOUNT — clicking × stores the announcement id in
 * the user's server-side preferences (`dismissedAnnouncementId`), so it
 * stays dismissed across sessions and devices. Publishing a NEW
 * announcement (new id) re-surfaces the banner for everyone.
 *
 * Auth-gated: pre-login there is no account to remember a dismissal
 * for (and no way to fetch the announcement), so nothing renders.
 */
export const BetaBanner: React.FC = () => {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  // Hides instantly on click, before the preferences PATCH lands.
  const [locallyDismissed, setLocallyDismissed] = useState<string | null>(null);

  const { data: announcement } = useQuery<Announcement | null>({
    queryKey: ['platform-announcement'],
    queryFn: async () => {
      const res = await apiClient.get<ApiEnvelope<Announcement | null>>(
        '/me/announcement',
      );
      return unwrap(res.data) ?? null;
    },
    enabled: isAuthenticated,
    staleTime: 5 * 60_000,
  });

  const profile = useMyProfile(isAuthenticated);
  const updatePrefs = useUpdateMyPreferences();

  const dismissedId =
    typeof profile.data?.preferences?.dismissedAnnouncementId === 'string'
      ? (profile.data.preferences.dismissedAnnouncementId as string)
      : null;

  // Wait for the profile before showing, so a user who already
  // dismissed doesn't get a flash of banner on every load.
  const visible = Boolean(
    isAuthenticated &&
      announcement &&
      profile.data &&
      dismissedId !== announcement.id &&
      locallyDismissed !== announcement.id,
  );

  useEffect(() => {
    setBannerHeightVar(visible ? BANNER_HEIGHT : '0px');
    return () => setBannerHeightVar('0px');
  }, [visible]);

  if (!visible || !announcement) return null;

  const dismiss = () => {
    setLocallyDismissed(announcement.id);
    updatePrefs.mutate({ dismissedAnnouncementId: announcement.id });
  };

  return (
    <div
      className="fixed top-0 left-0 right-0 z-[60] bg-gradient-to-r from-sky-500 via-blue-600 to-indigo-600 text-white shadow-md"
      style={{ height: BANNER_HEIGHT }}
    >
      <div className="h-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center gap-3">
        <Sparkles className="w-4 h-4 flex-shrink-0" aria-hidden />
        <p className="text-xs sm:text-sm leading-snug flex-1 min-w-0 truncate sm:whitespace-normal">
          {announcement.message}
        </p>
        <Button
          variant="ghost"
          size="icon-xs"
          type="button"
          onClick={dismiss}
          aria-label="Dismiss banner"
          className="flex-shrink-0 text-white hover:bg-white/15 hover:text-white"
        >
          <X />
        </Button>
      </div>
    </div>
  );
};

export default BetaBanner;
